'use client';

import type { DataGrailConsentCategory } from './types';

const POLL_INTERVAL_MS = 50;
const READY_TIMEOUT_MS = 2500;

function canReadConsent(): boolean {
  return typeof window !== 'undefined' && typeof window.DG_BANNER_API?.categoryEnabled === 'function';
}

/**
 * Whether the user granted a consent category.
 *
 * Fail-closed: DataGrail being blocked or not loaded yet reads as "not granted",
 * so callers degrade (e.g. non-personalized ads) rather than assuming consent.
 */
export function isConsentGranted(category: DataGrailConsentCategory): boolean {
  return window.DG_BANNER_API?.categoryEnabled?.(category) === true;
}

/**
 * Whether GAM requests must drop to non-personalized + restricted data processing.
 *
 * Fail-OPEN, unlike `isConsentGranted`: only a DataGrail that actually loaded
 * and reported marketing as off restricts the request. Blocked or never-loaded
 * DataGrail leaves ads personalized.
 *
 * This deliberately matches legacy hotpads-web
 * (`srcLegacy/shared/flux/actions/AdActions.ts`, which guards its downgrade on
 * `window.DG_BANNER_API &&`). Treating an unreadable DataGrail as a denial —
 * which is what this app did before — puts every ad-blocker user and everyone
 * whose DataGrail load loses the race onto NPA + RDP inventory, and that
 * inventory clears far below personalized. The first ad request still waits for
 * `waitForConsentApi`, so a real "no" is honored whenever DataGrail answers.
 */
export function isAdPersonalizationRestricted(): boolean {
  if (!canReadConsent()) return false;
  return !isConsentGranted('marketing');
}

export type WaitForConsentApiOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

/**
 * Resolves `true` once DataGrail can answer consent questions, or `false` when
 * `timeoutMs` passes without it loading (typically blocked by an ad blocker) or
 * the caller aborts.
 */
export function waitForConsentApi({ signal, timeoutMs = READY_TIMEOUT_MS }: WaitForConsentApiOptions = {}): Promise<
  boolean
> {
  if (canReadConsent()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let waited = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stop = (isReady: boolean) => {
      clearTimeout(timer);
      resolve(isReady);
    };

    const poll = () => {
      if (canReadConsent()) {
        stop(true);
        return;
      }

      waited += POLL_INTERVAL_MS;
      if (waited >= timeoutMs) {
        stop(false);
        return;
      }

      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    signal?.addEventListener('abort', () => stop(false), { once: true });
    timer = setTimeout(poll, POLL_INTERVAL_MS);
  });
}
